import React from 'react';
import { cn } from '../../lib/classNameHelpers';
import { formatNumber, formatPercentage } from '../../lib/formatters';
import { calcMidPriceFromOrderbook, calcSpreadPercentage } from '../../lib/orderbookHelpers';
import { orderbookMidPriceTooltip, orderbookOraclePriceTooltip } from '../../lib/tooltips';
import { BidsAsks } from '../../types/orderbook';
import { OraclePrice } from '../OraclePrice/OraclePrice';
import { Skeleton } from '../Skeleton/Skeleton';
import { Tooltip } from '../Tooltip/Tooltip';

/**
 * Small common components shared between orderbook layouts.
 */

export const OrderbookContainer: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div className={cn('flex size-full min-h-0 flex-col text-xs text-contentPrimary', className)}>
    {children}
  </div>
);

export const OrderbookHeader: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <div className="grid h-5 grid-cols-3 gap-x-3 px-2 pb-0 pt-1 text-2xs font-medium text-contentSecondary">
    {children}
  </div>
);

export const OrderbookRowsSection: React.FC<{
  children: React.ReactNode;
  sideBySide?: boolean;
  className?: string;
}> = ({ children, sideBySide = false, className }) => (
  <div
    className={cn(
      'flex flex-col gap-0.5 py-1 3xl:gap-[3px] 4xl:gap-1 5xl:gap-[3px]',
      sideBySide ? 'px-0.5' : 'px-1',
      className
    )}
  >
    {children}
  </div>
);

export const OrderbookBody: React.FC<{ children: React.ReactNode; error?: string | null }> = ({
  children,
  error,
}) => {
  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-4">
        <p className="text-xs font-medium text-contentTertiary">Failed to load orderbook.</p>
        <p className="text-xs text-contentSecondary">Try reloading the page.</p>
      </div>
    );
  }
  return children;
};

export const MidPriceSection = ({
  data,
  productId,
  priceDecimals,
}: {
  data?: BidsAsks;
  productId: string;
  priceDecimals: number;
}) => {
  const midPrice = calcMidPriceFromOrderbook(data);

  return (
    <div className={cn('flex items-center justify-between', data ? 'py-1 px-3' : 'px-1')}>
      {data ? (
        <>
          <div className="flex items-center gap-2">
            <Tooltip content={orderbookMidPriceTooltip()}>
              <span className="text-xs text-contentPrimary">
                {formatNumber(midPrice || undefined, {
                  maximumFractionDigits: priceDecimals,
                  minimumFractionDigits: priceDecimals,
                })}
              </span>
            </Tooltip>
            <Tooltip content={orderbookOraclePriceTooltip()}>
              <OraclePrice
                className="text-xs text-contentSecondary"
                productId={productId}
                priceDecimals={priceDecimals}
              />
            </Tooltip>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-3xs font-medium text-contentSecondary">
              Spread {formatPercentage(calcSpreadPercentage(data))}
            </span>
          </div>
        </>
      ) : (
        <Skeleton className="h-6 w-full" />
      )}
    </div>
  );
};
